import { Form, Formik } from "formik";
import * as Yup from "yup";
import AtriumApi, { getApiErrorMessage } from "api";
import AlertPane from "components/alert";
import { InputField, Select } from "components/fields";
import notify from "lib/notify";
import { track } from "lib/mixpanel";
import { EVENTS } from "lib/events";

const ROLE_OPTIONS = [
  { value: "staff", label: "Staff" },
  { value: "admin", label: "Admin" },
];

const PERMISSION_TEMPLATE_OPTIONS = [
  { value: "admin", label: "Admin: Full organization access" },
  { value: "staff_manager", label: "Staff Manager: People management and reports" },
  { value: "checkin_coordinator", label: "Check-in Coordinator: Check-in focused" },
  { value: "data_entry", label: "Data Entry: Pulse and attendance only" },
  { value: "staff", label: "Staff: View-only access" },
];

const TEMPLATE_DESCRIPTIONS = {
  super_admin: "Full system access for Atrium employees only.",
  admin: "Full organization access except billing.",
  staff_manager: "People management, reports and check-in.",
  checkin_coordinator: "Check-in focused, with view-only access to people.",
  data_entry: "Pulse and attendance entry only.",
  staff: "View-only access to most features.",
};

const validationSchema = Yup.object().shape({
  email: Yup.string().email("Invalid email format").required("Email is required"),
  role: Yup.string().required("Role is required"),
  permissionTemplate: Yup.string().required("Permission template is required"),
});

/**
 * Invitation form. Rendered inside InviteStaffDrawer, whose footer submits it
 * through the `formId` attribute.
 */
const InviteStaffForm = ({ formId, onClose, onSendingChange, onInviteSent }) => {
  const onSubmit = async (values) => {
    onSendingChange?.(true);

    try {
      const response = await AtriumApi.post("/v1/invitations", {
        email: values.email,
        role: values.role,
        permissionTemplate: values.permissionTemplate,
      });

      if (response.data.success) {
        track(EVENTS.SETTINGS_STAFF_INVITED);
        notify.success("Invitation sent");
        onInviteSent && onInviteSent();
        onClose();
      } else {
        throw new Error(response.data.message || "Could not send invitation");
      }
    } catch (error) {
      notify.error(getApiErrorMessage(error, "Could not send invitation"));
    } finally {
      onSendingChange?.(false);
    }
  };

  return (
    <Formik
      initialValues={{
        email: "",
        role: "staff",
        permissionTemplate: "staff",
      }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
    >
      {({ values, errors, touched, handleChange, handleBlur }) => (
        <Form id={formId} noValidate className="flex flex-col gap-5">
          <p className="text-sm text-fg-muted">
            Send an email invitation to a new staff member. They'll complete their profile and set
            up their account.
          </p>

          <InputField
            label="Email Address"
            required
            placeholder="staff@example.com"
            name="email"
            type="email"
            autoComplete="off"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.email}
            touched={touched.email}
          />

          <Select
            label="Role"
            required
            name="role"
            options={ROLE_OPTIONS}
            value={values.role}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.role}
            touched={touched.role}
          />

          <Select
            label="Permission Template"
            required
            name="permissionTemplate"
            placeholder="Select a permission template"
            options={PERMISSION_TEMPLATE_OPTIONS}
            hint={TEMPLATE_DESCRIPTIONS[values.permissionTemplate]}
            value={values.permissionTemplate}
            onChange={handleChange}
            onBlur={handleBlur}
            error={errors.permissionTemplate}
            touched={touched.permissionTemplate}
          />

          <AlertPane tone="info" className="">
            The user will receive an email with a link to complete their profile. The invitation
            expires in 7 days.
          </AlertPane>
        </Form>
      )}
    </Formik>
  );
};

export default InviteStaffForm;
